import { AwsRum } from "aws-rum-web";

let awsRum = null;
let rumInitAttempts = 0;
let lastRumInitAttemptAt = 0;
let lastRumInitError = null;
let hasWarnedMissingRumConfig = false;
const pendingEvents = [];
const DEFAULT_REGION = "us-east-1";
const RUM_INIT_RETRY_WINDOW_MS = 30000;
const RUM_MAX_INIT_ATTEMPTS = 20;
const RUM_DEBUG_ENABLED = process.env.REACT_APP_RUM_DEBUG === "true";

const now = () => Date.now();

const getEnvValue = (...keys) => {
	for (const key of keys) {
		const value = process.env[key];
		if (typeof value === "string" && value.trim().length > 0) {
			return value.trim();
		}
	}
	return null;
};

const flushPendingEvents = (rumClient) => {
	if (!rumClient || pendingEvents.length === 0) {
		return;
	}

	while (pendingEvents.length > 0) {
		const nextEvent = pendingEvents.shift();
		try {
			rumClient.recordEvent(nextEvent.name, nextEvent.payload);
		} catch (error) {
			console.error("RUM pending event flush failed:", error);
		}
	}
};

const readRumConfig = () => {
	const APPLICATION_ID = getEnvValue(
		"REACT_APP_RUM_APPLICATION_ID",
		"APPLICATION_ID"
	);
	const identityPoolId = getEnvValue(
		"REACT_APP_IDENTITY_POOL_ID",
		"identityPoolId",
		"REACT_APP_AWS_COGNITO_IDENTITY_POOL_ID"
	);
	const APPLICATION_REGION =
		getEnvValue("REACT_APP_RUM_REGION", "REACT_APP_AWS_REGION") ||
		DEFAULT_REGION;

	return {
		APPLICATION_ID,
		identityPoolId,
		APPLICATION_REGION,
	};
};

export const getRumDiagnostics = () => {
	const { APPLICATION_ID, identityPoolId, APPLICATION_REGION } =
		readRumConfig();
	const preview = (value) => {
		if (!value) {
			return null;
		}
		if (value.length <= 8) {
			return value;
		}
		return `${value.slice(0, 4)}...${value.slice(-4)}`;
	};

	return {
		initialized: Boolean(awsRum),
		initAttempts: rumInitAttempts,
		lastInitError: lastRumInitError,
		hasApplicationId: Boolean(APPLICATION_ID),
		hasIdentityPoolId: Boolean(identityPoolId),
		applicationIdPreview: preview(APPLICATION_ID),
		identityPoolIdPreview: preview(identityPoolId),
		applicationRegion: APPLICATION_REGION,
		pendingEvents: pendingEvents.length,
	};
};

export const emitRumTestEvent = (name = "rum_manual_test") => {
	const rumClient = awsRum || initRum({ forceRetry: true });
	if (!rumClient) {
		console.warn("RUM test event skipped: client not initialized yet");
		return false;
	}

	try {
		rumClient.recordEvent("rum_manual_test", {
			type: "Health",
			message: name,
		});
		return true;
	} catch (error) {
		console.error("RUM test event failed:", error);
		return false;
	}
};

const exposeDiagnostics = () => {
	if (typeof window === "undefined") {
		return;
	}

	window.__rumDiagnostics = getRumDiagnostics;
	window.__rumTestEvent = emitRumTestEvent;
};

// Expose diagnostics as soon as this module is imported so console helpers are always available.
exposeDiagnostics();

export const initRum = (options = {}) => {
	const { forceRetry = false } = options;

	if (awsRum) {
		return awsRum;
	}
	if (rumInitAttempts >= RUM_MAX_INIT_ATTEMPTS) {
		return null;
	}

	if (
		!forceRetry &&
		rumInitAttempts > 0 &&
		now() - lastRumInitAttemptAt < RUM_INIT_RETRY_WINDOW_MS
	) {
		return null;
	}
	rumInitAttempts += 1;
	lastRumInitAttemptAt = now();
	exposeDiagnostics();

	try {
		const { APPLICATION_ID, identityPoolId, APPLICATION_REGION } =
			readRumConfig();

		if (!APPLICATION_ID || !identityPoolId) {
			lastRumInitError =
				"Missing env values. Required: REACT_APP_RUM_APPLICATION_ID and REACT_APP_IDENTITY_POOL_ID";
			if (!hasWarnedMissingRumConfig) {
				console.warn(`RUM initialization skipped: ${lastRumInitError}`);
				hasWarnedMissingRumConfig = true;
			}
			return null;
		}

		const config = {
			sessionSampleRate: 1,
			identityPoolId,
			endpoint: `https://dataplane.rum.${APPLICATION_REGION}.amazonaws.com`,
			telemetries: ["performance", "errors", "http", "session"],
			allowCookies: true,
			enableXRay: false,
			signing: true,
			recordResourceUrl: true,
			dispatchInterval: 5000,
		};

		const APPLICATION_VERSION = "1.0.0";

		awsRum = new AwsRum(
			APPLICATION_ID,
			APPLICATION_VERSION,
			APPLICATION_REGION,
			config
		);
		lastRumInitError = null;
		console.log("RUM initialized successfully");
		if (RUM_DEBUG_ENABLED) {
			console.log("RUM diagnostics:", getRumDiagnostics());
		}

		// Emit a one-time marker event to verify ingestion quickly in test environments.
		awsRum.recordEvent("rum_client_init", {
			type: "Health",
			message: "RUM initialized",
		});
		flushPendingEvents(awsRum);
		return awsRum;
	} catch (error) {
		lastRumInitError = error?.message || "Unknown init error";
		console.error("RUM initialization failed:", error);
		return null;
	}
};

export const logErrorToCloudWatch = ({
	error_code,
	title,
	message,
	error_response,
}) => {
	try {
		const rumClient = awsRum || initRum({ forceRetry: true });
		const eventMessage = message || title || "Unknown error";
		const eventData = {
			type: "Error",
			error_code: error_code || "LC-UNKNOWN",
			message: eventMessage,
			error_response: error_response,
		};

		console.log("210 in rum.js", eventData);
		// Keep event details minimal and stable for CloudWatch metrics/alarms.
		if (!rumClient) {
			pendingEvents.push({ name: "fe_error", payload: eventData });
			console.warn("RUM not ready yet - queued fe_error event");
			return;
		}

		rumClient.recordEvent("fe_error", eventData);
		if (RUM_DEBUG_ENABLED) {
			console.log("RUM event emitted:", eventData);
		}
	} catch (error) {
		console.error("RUM event recording failed:", error);
	}
};

// Records a named, non-error event (e.g. "aura_run_completed"). Queued like fe_error when RUM
// isn't ready yet. Payloads must not carry PII or credentials.
export const logRumEvent = (name, payload = {}) => {
	try {
		const rumClient = awsRum || initRum({ forceRetry: true });
		if (!rumClient) {
			pendingEvents.push({ name, payload });
			return;
		}
		rumClient.recordEvent(name, payload);
		if (RUM_DEBUG_ENABLED) {
			console.log("RUM event emitted:", name, payload);
		}
	} catch (error) {
		console.error("RUM event recording failed:", error);
	}
};

export const logNumberedMessage = (number, message, data = null) => {
	console.log(`${number} ${message}`, data);

	logErrorToCloudWatch({
		error_code: `LOG-${number}`,
		message: `${number}: ${message}`,
	});
};
